/* assets/mcmarden-cart-sections-guard.js
   Fixes: changing a line item's quantity on the cart page updates that
   line's own total correctly, but the "Estimated total" in the cart
   footer never refreshes — it silently keeps showing the old number
   until the page is reloaded.

   Root cause, confirmed by reproducing it and inspecting the actual
   network response: Dawn's own cart.js (CartItems#updateQuantity) asks
   the server for four sections and then applies each one in a plain
   forEach:

     getSectionsToRender() → ['main-cart-items', 'cart-icon-bubble',
                               'cart-live-region-text', 'main-cart-footer']

     sectionsToRender.forEach((section) => {
       const elementToReplace =
         document.getElementById(section.id).querySelector(section.selector)
         || document.getElementById(section.id);
       elementToReplace.innerHTML = ...;
     });

   That `|| document.getElementById(section.id)` fallback only ever runs
   if the first expression returns a falsy value WITHOUT throwing — but
   `document.getElementById('cart-icon-bubble').querySelector(...)`
   throws outright when the element doesn't exist (TypeError: Cannot
   read properties of null), because `.querySelector` is called on
   `null` before the `||` gets a chance to step in. That's a latent bug
   in Dawn's own code, not something this fork changed.

   This theme's header is custom (sections/mcmarden-header.liquid) and
   was built without Dawn's `id="cart-icon-bubble"` element — the cart
   count badge is wired up separately in mcmarden-commerce.js's
   refreshCount(), by design (see that file's own comment on why). So on
   this theme, `document.getElementById('cart-icon-bubble')` is always
   null, that forEach iteration always throws, and — because a thrown
   error inside Array#forEach aborts every iteration after it — the two
   entries queued after it, 'cart-live-region-text' AND
   'main-cart-footer', never get applied. The estimated total lives in
   'main-cart-footer', which is why that's the one visibly broken.

   Fixed by patching CartItems.getSectionsToRender() to drop any entry
   whose target element genuinely isn't in this page's DOM, rather than
   editing cart.js directly (same reasoning as the rest of this theme's
   Dawn-collision fixes: a future Dawn update should be free to change
   this file without this patch silently reverting). This is a generic
   guard, not a hardcoded skip of 'cart-icon-bubble' specifically — it
   protects against the same crash for any section entry this theme
   doesn't render.

   Must load AFTER cart.js defines the `cart-items` custom element;
   placed directly below its <script> tag in
   sections/main-cart-items.liquid for that reason. Guarded with
   customElements.whenDefined() too, in case load order ever changes. */
(() => {
  "use strict";

  function patch() {
    const CartItems = customElements.get("cart-items");
    const proto = CartItems && CartItems.prototype;
    if (!proto || proto.getSectionsToRender.__mcmardenGuarded) return;

    const original = proto.getSectionsToRender;
    const guarded = function () {
      return original.call(this).filter((section) => document.getElementById(section.id));
    };
    guarded.__mcmardenGuarded = true;
    proto.getSectionsToRender = guarded;
  }

  if (customElements.get("cart-items")) {
    patch();
  } else {
    customElements.whenDefined("cart-items").then(patch);
  }
})();
